import React, { useState, useEffect, useRef } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import AgentChat from "../components/AgentChat";
import api from "../services/api";
import { hasUnrestrictedTargets } from "../utils/currentUser";
import useProjectStore, { runProjectId } from "../store/useProjectStore";
import Spinner from "../shared/shared-component/Spinner";
import { useBreadcrumb } from "../hooks/useBreadcrumb";

/**
 * AgentPage – full-page route for running an agent.
 *
 * Routes:
 *   /agent/:agentId                          → fresh run
 *   /agent/:agentId?executionId=<id>         → resume/view an existing execution
 *   /agent/:agentId?from=history             → came from history tab (back goes to /history)
 */
export default function AgentPage({ unrestrictedTargets }) {
  // The old UI threads this down from App; the new UI routes a bare
  // <AgentPage />. Fall back to the stored user so both behave the same
  // without the two route tables having to stay in step.
  const canEnterAnyUrl =
    unrestrictedTargets === undefined
      ? hasUnrestrictedTargets()
      : !!unrestrictedTargets;
  const { agentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const executionId = searchParams.get("executionId");
  const fromHistory = searchParams.get("from") === "history";
  const urlProjectId = searchParams.get("projectId") || null;

  // The run's project is the one the header shows, so it can be changed from
  // here instead of going back to the Agent Library for it. The URL — and, for
  // a run opened from history, the run's own project — only seeds that
  // selection; after the seed the header owns it.
  const selectedProjectId = useProjectStore((s) => s.selectedProjectId);
  const setSelectedProjectId = useProjectStore((s) => s.setSelectedProjectId);

  const [agent, setAgent] = useState(null);
  const [initialExecution, setInitialExecution] = useState(null);
  const [loadingAgent, setLoadingAgent] = useState(true);
  const [loadingExecution, setLoadingExecution] = useState(!!executionId);
  const [error, setError] = useState(null);
  const location = useLocation();

  useBreadcrumb(
    [
      { label: "Workspace", to: "/mainScreen" },
      fromHistory
        ? { label: "Execution History", to: "/history" }
        : { label: "Agents", to: "/agents" },
      { label: agent?.name || "Agent" },
    ],
    [agent?.name, fromHistory, location.pathname],
    { skip: loadingAgent },
  );

  // Load agent details
  useEffect(() => {
    let cancelled = false;
    setLoadingAgent(true);
    api
      .getAgentDetails(agentId)
      .then((res) => {
        if (!cancelled) {
          setAgent(res.data.agent);
          setLoadingAgent(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Error loading agent:", err);
          setError("Agent not found or could not be loaded.");
          setLoadingAgent(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  // Load execution if an executionId is provided
  useEffect(() => {
    if (!executionId) return;
    let cancelled = false;
    setLoadingExecution(true);
    api
      .getExecution(executionId)
      .then((res) => {
        if (!cancelled) {
          setInitialExecution(res.data.execution);
          setLoadingExecution(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Error loading execution:", err);
          setLoadingExecution(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [executionId]);

  // Seeded once per agent/execution, so switching project in the header is
  // not immediately overwritten by the id still sitting in the URL.
  const seededFor = useRef(null);
  useEffect(() => {
    const key = `${agentId}:${executionId || ""}`;
    if (seededFor.current === key) return;
    // An execution's project is only known once it has loaded.
    if (executionId && loadingExecution) return;
    seededFor.current = key;
    const seed =
      urlProjectId ||
      (initialExecution?.project ? String(initialExecution.project) : null);
    if (seed && seed !== selectedProjectId) setSelectedProjectId(seed);
  }, [
    agentId,
    executionId,
    loadingExecution,
    initialExecution,
    urlProjectId,
    selectedProjectId,
    setSelectedProjectId,
  ]);

  // Keep the URL in step with the header, so a reload (or a shared link) of a
  // chat whose project was switched here does not seed itself back to the
  // project it was opened with.
  useEffect(() => {
    const key = `${agentId}:${executionId || ""}`;
    if (seededFor.current !== key) return; // not seeded yet
    const runIn = runProjectId(selectedProjectId);
    if ((runIn || null) === urlProjectId) return;
    const next = new URLSearchParams(searchParams);
    if (runIn) next.set("projectId", runIn);
    else next.delete("projectId");
    setSearchParams(next, { replace: true });
  }, [
    agentId,
    executionId,
    selectedProjectId,
    urlProjectId,
    searchParams,
    setSearchParams,
  ]);

  const handleBack = () => {
    if (fromHistory) {
      navigate("/history");
    } else if (window.history.state && window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/agents");
    }
  };

  const isLoading = loadingAgent || loadingExecution;
  let content;

  if (isLoading) {
    content = (
      <div className="page">
        <div className="container">
          <Spinner text="agent Exec loading..." />
        </div>
      </div>
    );
  } else if (error || !agent) {
    content = (
      <div className="notFound text__center py__80__20">
        <p className="fb__16 fc__0E1218 mb__20">{error || "Agent not found"}</p>
        <button
          className="common__btn common__blk__btn"
          onClick={() => navigate("/agents")}
        >
          ← Back to Agents
        </button>
      </div>
    );
  } else {
    content = (
      <AgentChat
        key={executionId || "new"}
        agent={agent}
        onBack={handleBack}
        initialExecution={executionId ? initialExecution : null}
        fromHistory={fromHistory}
        onRunAgain={() =>
          setSearchParams({ projectId: runProjectId(selectedProjectId) || "" })
        }
        projectId={runProjectId(selectedProjectId)}
        // Without this the prop defaults to false, so the project gate fires
        // for the two kinds of user who are meant to bypass it entirely:
        // internal staff with free URL entry, and guests, who own no project
        // at all and could never get past it.
        unrestrictedTargets={canEnterAnyUrl}
      />
    );
  }

  return (
    <div className="wrapper__agent__execution wrapper__main">
      <div className="wrapper__container">{content}</div>
    </div>
  );
}
